import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  message,
  Input,
  Form,
  Select,
  Pagination,
} from "antd";
import { get, post } from "../../apiService";
import ReactQuill from "react-quill";
import { getProfile } from "../../services/localStorageServices";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import "./Review.css";
import moment from "moment/moment";
import { FaStar } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import { FaTrashAlt } from "react-icons/fa";
import TitleComp from "./TitleComp";
import TextArea from "antd/es/input/TextArea";

const { Option } = Select;

const Review = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [loading, setLoading] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [form] = Form.useForm(); // Define form variable
  const { profile } = getProfile();
  const [searchInput, setSearchInput] = useState("");

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (_, record) => moment(record.created_at).format("llll"),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <strong>
            {record.rate}&nbsp;
            <FaStar style={{ color: "gold" }} />
          </strong>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            className="bg-warning text-white"
            onClick={() => showUpdateModal(record)}
          >
            <FaEdit />
          </Button>
          <Button
            className="bg-danger text-white"
            onClick={() => showDeleteConfirm(record.review_id)}
          >
            <FaTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const dbounce = setTimeout(() => {
      fetchReviews(pagination.current, pagination.pageSize, searchInput);
    }, 300);
    return () => clearTimeout(dbounce);
  }, [searchInput]);

  const fetchReviews = async (current, pageSize, search) => {
    setLoading(true);
    try {
      const action = "fetch";
      const response = await get(
        `/review.php?limit=${pageSize}&page=${current}&action=${action}${
          search ? "&search=" + search + "" : ""
        }`
      );
      setReviews(response.list);
      setPagination((prevPagination) => ({
        ...prevPagination,
        current,
        pageSize,
        total: response.total,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showAddModal = () => {
    form.resetFields(); // Reset fields when opening the modal
    setIsAddModalVisible(true);
  };

  const handleAdd = async (values) => {
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("rate", values.rate);
      formData.append("action", "add");
      formData.append("user_id", profile.user_id);
      const response = await post("/review.php", formData);
      if (response) {
        message.success("Review added successfully");
        fetchReviews(pagination.current, pagination.pageSize);
        setIsAddModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to add review");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while adding the review");
    }
  };

  const showUpdateModal = (record) => {
    setSelectedReview(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      rate: record.rate,
    });
    setIsUpdateModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const formData = new FormData();
      formData.append("review_id", selectedReview.review_id);
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("rate", values.rate);
      formData.append("action", "update");
      const response = await post("/review.php", formData);
      if (response) {
        message.success("Review updated successfully");
        fetchReviews(pagination.current, pagination.pageSize);
        setIsUpdateModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to update review");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while updating the review");
    }
  };

  const showDeleteConfirm = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this review?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => handleDelete(id),
    });
  };

  const handleDelete = async (id) => {
    try {
      const formData = new FormData();
      formData.append("review_id", id);
      formData.append("action", "update");
      formData.append("is_active", "0");

      const response = await post("/review.php", formData);
      if (response) {
        message.success("Review deleted successfully");
        fetchReviews(pagination.current, pagination.pageSize);
      } else {
        message.error("Failed to delete review");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while deleting the review");
    }
  };

  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;

    setPagination({
      ...pagination,
      current,
      pageSize,
    });

    if (pageSize !== pagination.pageSize) {
      fetchReviews(1, pageSize);
    } else {
      fetchReviews(current, pageSize);
    }
  };

  return (
    <div className="p-3">
      <div>
        <TitleComp
          name="Review List"
          button={
            <Button
              className="orderFilter-serach-btn"
              onClick={showAddModal}
              style={{ marginBottom: "16px" }}
            >
              Add Review
            </Button>
          }
          type="Review"
          totalData={pagination.total}
          setSearchInput={setSearchInput}
        />

        {/* Table Container */}
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={reviews}
            pagination={false}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>

        {/* Card Container */}
        <div className="card-container">
          {reviews?.map((review) => (
            <div
              className="card"
              style={{ maxWidth: "unset" }}
              key={review.review_id}
            >
              <div className="card-title">{review.title}</div>
              <div className="card-description">{review.description}</div>
              <div className="card-rate d-flex align-items-center">
                Rate: {review.rate} <FaStar style={{ color: "gold" }} />
              </div>
              <div className="">
                Created At: {moment(review.created_at).format("llll")}
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <Button
                  className="bg-warning text-white"
                  onClick={() => showUpdateModal(review)}
                >
                  <FaEdit />
                </Button>
                <Button
                  className="bg-danger text-white"
                  onClick={() => showDeleteConfirm(review.review_id)}
                >
                  <FaTrashAlt />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Pagination
          className="mt-4"
          current={pagination.current}
          total={pagination.total}
        />

        {/* Add Modal */}
        <Modal
          title="Add Review"
          visible={isAddModalVisible}
          onCancel={() => setIsAddModalVisible(false)}
          footer={null}
        >
          <Form
            form={form} // Attach form instance
            layout="vertical"
            onFinish={handleAdd}
          >
            <Form.Item
              label="Client Name"
              name="title"
              rules={[{ required: true, message: "Please enter the title" }]}
            >
              <Input placeholder="Name" />
            </Form.Item>

            <Form.Item
              label="Write Review"
              name="description"
              rules={[
                { required: true, message: "Please enter the description" },
              ]}
            >
              <TextArea placeholder="Review" rows={5} />
            </Form.Item>

            <Form.Item
              label="Rate"
              name="rate"
              rules={[{ required: true, message: "Please select a rate" }]}
            >
              <Select placeholder="Select Rate">
                {[1, 2, 3, 4, 5]?.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="action" hidden>
              <Input type="hidden" value="add" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Add
              </Button>
              <Button
                type="default"
                onClick={() => setIsAddModalVisible(false)}
                style={{ marginLeft: "8px" }}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        {/* Update Modal */}
        <Modal
          title="Update Review"
          visible={isUpdateModalVisible}
          onCancel={() => setIsUpdateModalVisible(false)}
          footer={null}
        >
          {selectedReview && (
            <Form
              form={form} // Attach form instance
              layout="vertical"
              initialValues={{
                title: selectedReview.title,
                description: selectedReview.description,
                rate: selectedReview.rate,
              }}
              onFinish={handleUpdate}
            >
              <Form.Item
                label="Client Name"
                name="title"
                rules={[{ required: true, message: "Please enter the title" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Write Review"
                name="description"
                rules={[
                  { required: true, message: "Please enter the description" },
                ]}
              >
                <TextArea placeholder="Review" rows={5} />
              </Form.Item>

              <Form.Item
                label="Rate"
                name="rate"
                rules={[{ required: true, message: "Please select a rate" }]}
              >
                <Select>
                  {[1, 2, 3, 4, 5]?.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="action" hidden>
                <Input type="hidden" value="update" />
              </Form.Item>

              <Form.Item>
                <Button className="orderFilter-serach-btn" htmlType="submit">
                  Update
                </Button>
                <Button
                  type="default"
                  onClick={() => setIsUpdateModalVisible(false)}
                  style={{ marginLeft: "8px" }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Review;
